import React from 'react';
import { Star, MessageCircle, Repeat2, Rocket, Star as StarIcon, Sparkles, Music2 } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { useNavigate } from 'react-router-dom';
import { resolveReactionState } from '../lib/socialUtils';

function fmt(n) { return n >= 1000 ? (n / 1000).toFixed(1).replace('.0', '') + 'k' : n; }

function relTime(iso) {
  if (!iso) return '';
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  return `${Math.floor(hrs / 24)}d`;
}

export default function PostCard({ post, showPinned = false, onReactionChange }) {
  const navigate = useNavigate();
  const { toggleStar, starred } = useApp();
  const isStar = resolveReactionState(starred[post.id], post.reactedByMe);

  const handleStar = async () => {
    const nowStarred = await toggleStar(post.id, isStar);
    onReactionChange?.(nowStarred ? 1 : -1);
  };

  return (
    <article className="rise-in orbit-surface-card mx-4 my-4">
      {showPinned && post.pinned && (
        <div className="flex items-center gap-2 mb-3 ml-1" style={{ color: '#9a90c4' }}>
          <span className="flex items-center justify-center rounded-full" style={{ width: 26, height: 26, border: '1.5px solid #9a90c4' }}>
            <StarIcon size={13} fill="#c9bcf0" color="#c9bcf0" />
          </span>
          <span className="text-sm">Fixed broadcast</span>
        </div>
      )}
      <div className="flex gap-3">
        <div className="relative shrink-0">
          {post.avatar ? (
            <img src={post.avatar} alt={post.authorHandle}
              className="rounded-full object-cover" style={{ width: 44, height: 44, border: '1px solid rgba(160,140,230,.4)' }} />
          ) : (
            <div className="rounded-full flex items-center justify-center font-bold" style={{ width: 44, height: 44, background: 'linear-gradient(135deg,#ff5bd1,#6b4fd6)', color: '#fff' }}>
              {(post.authorName || post.authorHandle || '?')[0]?.toUpperCase()}
            </div>
          )}
          <span className="absolute bottom-0 right-0 rounded-full" style={{ width: 11, height: 11, background: '#37e07a', border: '2px solid #0a0b1e' }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold" style={{ color: '#efeaff' }}>@{post.authorHandle}</span>
            <span className="text-sm" style={{ color: '#7d769f' }}>{relTime(post.time)}</span>
            {post.audience === 'galaxy' && (
              <span className="inline-flex items-center gap-1 text-sm font-semibold text-glow-pink" style={{ color: '#ff8fe4' }}>
                <Sparkles size={14} /> galaxy
              </span>
            )}
          </div>
          {post.text && <p className="mt-1 leading-snug" style={{ color: '#ece8fb', fontSize: 17 }}>{post.text}</p>}

          {post.feel && <p className="text-sm mt-1" style={{ color: '#b7a5ff' }}>feeling {post.feel} {post.location ? `· ${post.location}` : ''}</p>}

          {post.image && (
            <div className="mt-3 overflow-hidden" style={{ borderRadius: 22, border: '1px solid rgba(255,255,255,.08)' }}>
              <img src={post.image} alt="art" className="w-full object-cover" style={{ maxHeight: 380 }} />
            </div>
          )}

          {post.poll && (
            <div className="mt-3 space-y-2">
              {post.poll.options.filter(Boolean).map((opt) => (
                <div key={opt.id} className="px-4 py-2 rounded-full text-sm" style={{ border: '1.5px solid rgba(255,157,224,.6)', color: '#ffd9f3' }}>{opt.label}</div>
              ))}
            </div>
          )}

          {post.song?.title && (
            <div className="mt-3 flex items-center gap-3 p-2 pr-4 rounded-2xl" style={{ background: 'rgba(108,95,191,.18)', border: '1px solid rgba(139,124,232,.3)' }}>
              <div className="min-w-0">
                <div className="flex items-center gap-1 text-sm font-semibold" style={{ color: '#fff' }}><Music2 size={13} /> {post.song.title}</div>
                {post.song.artist && <div className="text-xs" style={{ color: '#b7a5ff' }}>{post.song.artist}</div>}
              </div>
            </div>
          )}

          <div className="flex items-center justify-between gap-2 mt-4" style={{ color: '#a89fc9' }}>
            <button onClick={handleStar} className="press orbit-action-button"
              style={{ color: isStar ? '#ffd25b' : '#a89fc9' }} aria-label="Star broadcast">
              <Star fill={isStar ? '#ffd25b' : 'none'} /> <span>{fmt(post.counts.stars)}</span>
            </button>
            <button onClick={() => navigate(`/posts/${post.id}/comments`)} className="press orbit-action-button" aria-label="Open comments">
              <MessageCircle /> <span>{fmt(post.counts.echoes)}</span>
            </button>
            <button className="press orbit-action-button" style={{ color: '#8b7ce8' }} disabled aria-label="Relays">
              <Repeat2 /> <span>{fmt(post.counts.relays)}</span>
            </button>
            <button className="press orbit-action-button" style={{ color: '#8b7ce8' }} disabled aria-label="Rockets">
              <Rocket /> <span>{fmt(post.counts.rockets)}</span>
            </button>
          </div>
        </div>
      </div>
    </article>
  );
}
